import { GET_ALL_COMPANIES } from 'src/api/graphql/queries/companies.gql';
import { fetchList } from './fetchLists';
import {
	intersectIdLists,
	pickFilters,
	prepareGraphqlFilters,
} from 'src/utils/helpers/prepareGraphqlFilters';
import { toSalesTargetEnum } from 'src/utils/mappers/companySalesTarget.mapper';
import { mapGorkoStatusFilterToGraphql } from 'src/utils/mappers/companyGorkoStatus.mapper';

const mapLinkedCompany = (company) => ({
	id: company.id,
	gorkoId: company.gorkoId,
	name: company.name,
	city: company.city,
	worth: company.worth,
	isGorkoCommission: company.isGorkoCommission,
	isGorkoAbonent: company.isGorkoAbonent,
	isGorkoHidden: company.isGorkoHidden,
	isGorkoDeleted: company.isGorkoDeleted,
	salesTarget: company.salesTarget,
	commissionPriority: company.commissionPriority,
});

const getNeighborCompanies = (company) => {
	const linked = [];
	const seen = new Set();

	const addCompany = (item) => {
		if (!item?.id || seen.has(item.id) || item.id === company.id) return;

		seen.add(item.id);
		linked.push(mapLinkedCompany(item));
	};

	if (company.isGroup) {
		for (const link of company.groupLinksAsGroup || []) {
			if (!link.unlinked) {
				addCompany(link.company);
			}
		}

		return linked;
	}

	for (const memberLink of company.groupLinksAsMember || []) {
		if (memberLink.unlinked) continue;

		for (const groupLink of memberLink.group?.groupLinksAsGroup || []) {
			if (groupLink.unlinked) continue;

			addCompany(groupLink.company);
		}
	}

	return linked;
};

const typeKeys = [
	'isGorkoCommission',
	'isGorkoAbonent',
	'isAccountManagement',
	'isContentManagement',
];

const asEnumList = (value) =>
	(Array.isArray(value) ? value : [value])
		.map(toSalesTargetEnum)
		.filter(Boolean);

export const buildCompanyTypeFilters = (types = {}) => {
	const filters = {};

	typeKeys.forEach((key) => {
		if (types[key] != null) {
			filters[key] = { eq: types[key] };
		}
	});

	if (types.salesTarget) {
		filters.salesTarget = { eq: asEnumList(types.salesTarget) };
	}

	if (types.commissionPriority) {
		filters.commissionPriority = {
			eq: Array.isArray(types.commissionPriority)
				? types.commissionPriority
				: [types.commissionPriority],
		};
	}

	return filters;
};

export const getAllCompanies = async ({
	page,
	limit,
	statusIds,
	menuFilter,
	types,
	filters,
	sort,
}) => {
	const basefilters = {
		...buildCompanyTypeFilters(types),
		...(menuFilter || {}),
	};
	const clonedFilters = pickFilters(prepareGraphqlFilters(filters), [
		'id',
		'gorkoId',
		'name',
		'cityName',
		'worthValue',
		'status',
		'statusCode',
		'statusAt',
		'updatedAt',
		'gorkoStatus',
		'isGorkoCommission',
		'isGorkoAbonent',
		'isGorkoDeleted',
		'isGorkoHidden',
		'salesTarget',
		'commissionPriority',
		'unsolvedObjectionsCount',
		'latestUnsolvedObjectionCreatedAt',
		'postponedUntil',
	]);

	if (clonedFilters.gorkoStatus) {
		Object.assign(
			clonedFilters,
			mapGorkoStatusFilterToGraphql(clonedFilters.gorkoStatus),
		);
		delete clonedFilters.gorkoStatus;
	}

	if (clonedFilters.status?.eq && statusIds?.length) {
		const eq = intersectIdLists(clonedFilters.status.eq, statusIds);
		clonedFilters.status = { eq: eq.length ? eq : [-1] };
	} else if (statusIds?.length) {
		basefilters.status = { eq: statusIds };
	}

	if (clonedFilters.salesTarget?.eq) {
		clonedFilters.salesTarget.eq = asEnumList(clonedFilters.salesTarget.eq);

		if (basefilters.salesTarget?.eq?.length) {
			const allowed = basefilters.salesTarget.eq;
			const eq = clonedFilters.salesTarget.eq.filter((value) =>
				allowed.includes(value),
			);

			clonedFilters.salesTarget = { eq: eq.length ? eq : allowed };
			delete basefilters.salesTarget;

			if (!eq.length) {
				clonedFilters.id = { eq: [-1] };
			}
		}
	}

	return fetchList({
		query: GET_ALL_COMPANIES,
		variables: {
			page,
			limit,
			filters: {
				...basefilters,
				...clonedFilters,
			},
			sort,
		},
		transformItems: (items) => {
			return items.map((company) => ({
				...company,
				groupLinksAsGroup: getNeighborCompanies(company),
			}));
		},
		addPerPage: true,
	});
};
